import { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { tooltipStyle, COLORS } from '../../utils/colors';
import { useColorScheme } from '../../hooks/useColorScheme';

Chart.register(ChartDataLabels);

/**
 * Horizontal bar chart showing average allocation per option.
 * When filters are active, shows overall (lighter) overlaid behind filtered (solid).
 *
 * Props:
 *   data.overall  — [{ optionText, avgValue }]
 *   data.filtered — [{ optionText, avgValue }] or null
 */
export default function AllocationChart({ data }) {
  const { palette } = useColorScheme();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data?.overall) return;

    if (chartRef.current) chartRef.current.destroy();

    const labels = data.overall.map(d => truncateLabel(d.optionText, 45));
    const fullLabels = data.overall.map(d => d.optionText);
    const overallValues = data.overall.map(d => d.avgValue);
    const hasFilter = data.filtered && data.filtered.length > 0;
    const filteredValues = hasFilter ? data.filtered.map(d => d.avgValue) : null;

    const datasets = [];

    if (hasFilter) {
      // Overall bars (lighter, behind)
      datasets.push({
        label: 'Overall',
        data: overallValues,
        backgroundColor: overallValues.map((_, i) => hexToRgba(palette[i % palette.length], 0.25)),
        borderColor: overallValues.map((_, i) => hexToRgba(palette[i % palette.length], 0.4)),
        borderWidth: 1,
        borderRadius: 6,
        barPercentage: 0.7,
        categoryPercentage: 0.8
      });
      // Filtered bars (solid, in front)
      datasets.push({
        label: 'Filtered',
        data: filteredValues,
        backgroundColor: filteredValues.map((_, i) => palette[i % palette.length]),
        borderColor: filteredValues.map((_, i) => palette[i % palette.length]),
        borderWidth: 0,
        borderRadius: 6,
        barPercentage: 0.5,
        categoryPercentage: 0.8
      });
    } else {
      datasets.push({
        label: 'Average',
        data: overallValues,
        backgroundColor: overallValues.map((_, i) => palette[i % palette.length]),
        borderWidth: 0,
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.8
      });
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        _fullLabels: fullLabels,
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            grid: { color: '#f0f1f3' },
            ticks: { font: { size: 11 }, color: COLORS.textMuted }
          },
          y: {
            grid: { display: false },
            ticks: {
              font: { size: 12, weight: 500 },
              color: COLORS.textSecondary,
              padding: 8
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipStyle,
            callbacks: {
              title: (items) => fullLabels[items[0].dataIndex],
              label: (item) => ` ${item.dataset.label}: ${item.raw.toFixed(1)}`
            }
          },
          datalabels: {
            display: true,
            anchor: 'end',
            align: 'right',
            color: COLORS.textSecondary,
            font: { size: 11, weight: 600 },
            formatter: (v) => v.toFixed(1)
          }
        }
      }
    });

    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [data, palette]);

  return <canvas ref={canvasRef} />;
}

function truncateLabel(text, maxLen) {
  if (!text) return '';
  return text.length > maxLen ? text.slice(0, maxLen - 3) + '...' : text;
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
